import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { memo, useRef } from 'react';
import { useButton } from 'react-aria';

import { mergeRefs } from '../../lib/mergeRefs';
import { isMACOS } from '../../lib/platform';
import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';
import { ContextMenu } from '../common/context-menu/ContextMenu';
import { Glyph } from '../common/Glyph';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useAdvancedFlow } from '../../store/advanced-flow/useAdvancedFlows';
import { useApi } from '../../store/SelectedHomeyStore.hooks';

import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { motion } from '../../theme/motion';
import { theme } from '../../theme/theme';
import glyphStyles from '../common/Glyph.module.css';

import { AdvancedFlowContextMenuContent } from './AdvancedFlowContextMenuContent';
import { useFlowDragDrop } from './useFlowDragDrop';

export const MemoizedAdvancedFlowTile = memo(function (props) {
  const triggerButtonRef = useRef();
  const buttonRef = useRef();
  const instanceRef = useRef({
    time: null,
  });

  const { i18n } = useI18n();
  const { api } = useApi();
  const { advancedFlow, isLoading, error } = useAdvancedFlow({
    advancedFlowId: props.advancedFlowId,
  });

  function triggerAdvancedFlow() {
    (async () => {
      if (advancedFlow.triggerable === true) {
        try {
          await api.flow.triggerAdvancedFlow({
            id: advancedFlow.id,
          });
          ToastManager.add({
            icon: glyph.system.flow,
            message: i18n.messageFormatter(`flow.started`, {
              name: advancedFlow.name,
            }),
          });
        } catch (error) {
          ToastManager.handleError(error);
        }
      }
    })().catch(console.error);
  }

  function handlePress(event) {
    if (event.altKey) {
      triggerAdvancedFlow();
      return;
    }

    RouteManager.toAdvancedFlow(advancedFlow.id);
  }

  const button = useButton(
    {
      elementType: 'div',
      onPressStart(event) {
        instanceRef.current.time = Date.now();
      },
      onPress(event) {
        if (event.ctrlKey && isMACOS === true) return;

        const ms = Date.now() - (instanceRef.current.time ?? 0);

        if (ms > 300 && props.onLongPress != null) {
          props.onLongPress(event);
        } else {
          handlePress(event);
        }
      },
    },
    buttonRef,
  );

  const triggerButton = useButton(
    {
      'aria-label': i18n.messageFormatter('common.start'),
      isDisabled: advancedFlow?.triggerable !== true,
      onPress() {
        triggerAdvancedFlow();
      },
    },
    triggerButtonRef,
  );

  const { dragRef, dropRef, isDragging } = useFlowDragDrop({
    flowId: advancedFlow?.id,
    isDisabled: props.isDragDropDisabled,
    onDrop: props.onDrop,
    onDragEnd: props.onDragEnd,
    onRequestSwap: props.onRequestSwap,
    render() {
      return (
        <MemoizedAdvancedFlowTile.Root data-is-drag-preview={true}>
          <MemoizedAdvancedFlowTile.TriggerButton
            data-is-disabled={advancedFlow?.triggerable !== true}
          >
            <Glyph
              source={glyph.core.play}
              color={theme.color.white}
              size={theme.icon.size_small}
            />
          </MemoizedAdvancedFlowTile.TriggerButton>
          <MemoizedAdvancedFlowTile.Name title={advancedFlow?.name}>
            {advancedFlow?.name}
          </MemoizedAdvancedFlowTile.Name>
        </MemoizedAdvancedFlowTile.Root>
      );
    },
  });

  if (advancedFlow == null && isLoading === false && error == null) {
    return (
      <MemoizedAdvancedFlowTile.Root
        title={props.advancedFlowId}
        data-is-unknown={true}
        data-is-interaction-disabled={true}
      >
        <Glyph
          size={theme.icon.size_medium}
          color={theme.icon.color_body_light}
          source={glyph.core.exclamationmarkTriangleFill}
        />
      </MemoizedAdvancedFlowTile.Root>
    );
  }

  if (isLoading === true) {
    return (
      <MemoizedAdvancedFlowTile.Root>
        <MemoizedAdvancedFlowTile.TriggerButton
          data-is-disabled={advancedFlow?.triggerable !== true}
        >
          <Glyph source={glyph.core.play} color={theme.color.white} size={theme.icon.size_small} />
        </MemoizedAdvancedFlowTile.TriggerButton>
        <MemoizedAdvancedFlowTile.Name />
      </MemoizedAdvancedFlowTile.Root>
    );
  }

  if (advancedFlow == null) {
    return null;
  }

  return (
    <ContextMenu
      content={
        <AdvancedFlowContextMenuContent
          advancedFlowId={advancedFlow.id}
          onTriggerRequest={() => {
            triggerAdvancedFlow();
          }}
        />
      }
    >
      {({ isOpen, onContextMenu }) => {
        return (
          <MemoizedAdvancedFlowTile.Root
            {...button.buttonProps}
            ref={mergeRefs([dragRef, dropRef, buttonRef])}
            aria-haspopup="menu"
            aria-expanded={props.isOpen}
            data-is-dragging={isDragging}
            onContextMenu={onContextMenu}
          >
            <MemoizedAdvancedFlowTile.TriggerButton
              {...triggerButton.buttonProps}
              ref={triggerButtonRef}
            >
              <Glyph
                source={glyph.core.play}
                color={theme.color.white}
                size={theme.icon.size_small}
              />
            </MemoizedAdvancedFlowTile.TriggerButton>

            <MemoizedAdvancedFlowTile.Name
              href={RouteManager.getPathForAdvancedFlow(advancedFlow.id)}
              title={advancedFlow.name}
              onClick={(event) => {
                event.preventDefault();
              }}
            >
              {advancedFlow.name}
            </MemoizedAdvancedFlowTile.Name>
          </MemoizedAdvancedFlowTile.Root>
        );
      }}
    </ContextMenu>
  );
});

MemoizedAdvancedFlowTile.Root = styled.div`
  --transform-transition: transform ease-in-out ${motion.duration.fast};

  display: inline-flex;
  align-items: center;
  min-width: 120px;
  max-width: var(--tmp-node-width, var(--node-width, 300px));
  width: var(--tmp-node-width, var(--node-width));
  height: var(--tmp-node-height, var(--node-height, ${su(5)}));
  padding-inline-start: ${su(0.75)};
  padding-inline-end: ${su(1)};
  border-radius: ${theme.borderRadius.large};
  background-color: ${theme.color.component_highlight};
  box-shadow: ${theme.boxShadow.default};
  cursor: pointer;
  transition: var(--transform-transition);

  &:hover {
    transform: scale(1.05);

    &:active:not(:focus-within) {
      transform: scale(1);
    }
  }

  &[data-is-interaction-disabled='true'] {
    cursor: default;

    &:hover {
      transform: initial;

      &:active:not(:focus-within) {
        transform: initial;
      }
    }
  }

  &[data-is-unknown='true'] {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(221, 222, 226, 0.5);
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='20' ry='20' stroke='rgb(151, 151, 151)' stroke-width='4' stroke-dasharray='10%2c 10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
  }

  &[data-is-drag-preview='true'] {
    transform: scale(0.95);
    animation: ${keyframes`
      from {
        transform: scale(1);
      }

      to {
        transform: scale(0.95);
      }
    `} ${motion.duration.slow} ease-out forwards 1;
  }

  &[data-is-dragging='true'] {
    opacity: 0;
  }
`;

MemoizedAdvancedFlowTile.TriggerButton = styled.button`
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${theme.color.success};
  transition: var(--transform-transition);

  &[data-is-disabled='true'],
  &[disabled] {
    background-color: ${theme.color.text_disabled};
  }

  &:hover:not([data-is-disabled='true']) {
    transform: scale(1.05);

    &:active:not([data-is-disabled='true']) {
      transform: scale(1);
    }
  }

  .${glyphStyles.root} {
    transform: translate(1px, 0);
  }
`;

MemoizedAdvancedFlowTile.Name = styled.a`
  display: inline-block;
  padding-inline-end: ${su(1)};
  padding-inline-start: ${su(1)};
  overflow: hidden;
  font-size: ${theme.fontSize.small};
  text-overflow: ellipsis;
  white-space: nowrap;
  color: inherit;
  text-decoration: none;
`;
